import { ColumnDef } from "@tanstack/react-table";

import { Badge, BadgeVariantType } from "@/components/ui/badge";
import DashboardActionCell from "@/components/ui/DashboardActionCell";
import { TableSortedHeader } from "@/components/ui/table";
import TimeAgo from "@/components/ui/TimeAgo";
import { DataSchemaType, formatDate } from "@/utils/date";

interface ColumnOptions<T> {
  key: keyof T;
  title: string;
  enableSorting?: boolean;
  maxSize?: number;
}

interface Timezone {
  code: string;
  name: string;
}

interface CreateColumnsOptions<T> {
  statusMappings: {
    variant: Record<string, string>;
    label: Record<string, string>;
  };
  handleViewAction?: (data: T) => void;
  handleEditAction?: (data: T) => void;
  handleDeleteAction?: (data: T) => void;
  viewPermission?: boolean;
  editPermission?: boolean;
  deletePermission?: boolean;
}

interface CreateColumnsOutput<T> {
  textColumn: (options: ColumnOptions<T>) => ColumnDef<T, string>;
  dateColumn: (
    options: ColumnOptions<T> & { timezone?: Timezone }
  ) => ColumnDef<T, string>;
  lastActiveColumn: (options: ColumnOptions<T>) => ColumnDef<T, string>;
  statusColumn: (
    options: Omit<ColumnOptions<T>, "enableSorting" | "key" | "title">
  ) => ColumnDef<T, string>;
  actionColumn: (
    options: Omit<ColumnOptions<T>, "enableSorting" | "key" | "title">
  ) => ColumnDef<T, string>;
  customColumn: <K extends keyof T>(
    key: K,
    title: string,
    options?: Omit<ColumnDef<T, string>, K>
  ) => ColumnDef<T, string>;
}

/**
 * Factory for creating common table columns
 */
export function createColumns<T>(
  options: CreateColumnsOptions<T>,
  timezone?: {
    code: string;
    name: string;
  }
): CreateColumnsOutput<T> {
  const {
    statusMappings,
    handleViewAction,
    handleEditAction,
    handleDeleteAction,
    viewPermission,
    editPermission,
    deletePermission
  } = options;
  return {
    // Text columns with sorting
    textColumn: ({
      key,
      title,
      enableSorting = true,
      maxSize = 200
    }): ColumnDef<T, string> => ({
      accessorKey: key as string,
      header: ({ column }) => {
        if (enableSorting) {
          return <TableSortedHeader column={column} title={title} />;
        }
        return <p className="text-txtBody text-xs font-semibold">{title}</p>;
      },
      cell: ({ getValue }) => (
        <p className="truncate text-xs" title={getValue()}>
          {getValue()}
        </p>
      ),
      enableSorting,
      maxSize
    }),

    // Date columns with specific formatting
    dateColumn: ({
      key,
      title,
      enableSorting = true,
      maxSize = 200
    }): ColumnDef<T, string> => ({
      accessorKey: key,
      header: ({ column }) => (
        <TableSortedHeader column={column} title={title} />
      ),
      cell: ({ getValue }) => (
        <p className="text-txtBody text-xs">
          {formatDate(getValue(), DataSchemaType.DATE_TIME, timezone)}
        </p>
      ),
      enableSorting,
      maxSize
    }),

    // Last active column with time ago formatting
    lastActiveColumn: ({
      key,
      title,
      enableSorting = true,
      maxSize = 200
    }): ColumnDef<T, string> => ({
      accessorKey: key,
      header: ({ column }) => (
        <TableSortedHeader column={column} title={title} />
      ),
      cell: ({ getValue }) => (
        <p className="text-txtBody text-xs">
          <TimeAgo date={getValue()} />
        </p>
      ),
      enableSorting,
      maxSize
    }),

    // Status column with badge
    statusColumn: ({ maxSize = 200 }): ColumnDef<T, string> => ({
      accessorKey: "status",
      header: ({ column }) => (
        <TableSortedHeader column={column} title="Status" />
      ),
      cell: ({ getValue }) => (
        <Badge variant={statusMappings.variant[getValue()] as BadgeVariantType}>
          {statusMappings.label[getValue()]}
        </Badge>
      ),
      enableSorting: true,
      maxSize
    }),

    // Action column
    actionColumn: ({ maxSize = 62 }): ColumnDef<T, string> => ({
      id: "action",
      accessorKey: "action",
      header: () => (
        <p className="text-txtBody text-xs font-semibold">Action</p>
      ),
      cell: (data: { row: { original: T } }) => (
        <DashboardActionCell<T>
          data={data.row.original}
          onViewClick={handleViewAction}
          onEditClick={handleEditAction}
          onDeleteClick={handleDeleteAction}
          viewPermission={viewPermission}
          editPermission={editPermission}
          deletePermission={deletePermission}
        />
      ),
      maxSize
    }),

    // Custom renderer for any special case
    customColumn: <K extends keyof T>(
      key: K,
      title: string,
      options?: Omit<ColumnDef<T, string>, K>
    ): ColumnDef<T, string> => ({
      id: key as string,
      accessorKey: key as string,
      header: ({ column }) => (
        <TableSortedHeader column={column} title={title} />
      ),
      ...options
    })
  };
}
